import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
} from '@mui/material';
import {
  NotificationsOutlined as BellIcon,
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  WarningOutlined as WarningIcon,
  ErrorOutlined as ErrorIcon,
} from '@mui/icons-material';
import { useNotifications } from '../utils/notifications';

/**
 * Notification Bell Component with real-time updates
 * Displays notifications in a dropdown popover
 */
const NotificationBell = ({ candidateId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const { notify } = useNotifications(candidateId);

  // Simulated notifications (Replace with real API polling or WebSocket)
  useEffect(() => {
    const interval = setInterval(() => {
      // This would be replaced with actual API call or WebSocket listener
      // For demo, we'll add a notification every 30 seconds
      const demoNotifications = [
        { id: 1, type: 'success', message: 'New assessment available!', timestamp: new Date() },
        { id: 2, type: 'info', message: 'Your profile is 80% complete', timestamp: new Date() },
      ];

      // Uncomment to test notifications
      // setNotifications(prev => [...demoNotifications, ...prev].slice(0, 5));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const clearAll = () => {
    setNotifications([]);
    handleClose();
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const open = Boolean(anchorEl);
  const unreadCount = notifications.length;

  return (
    <>
      <IconButton onClick={handleClick} color="inherit">
        <Badge badgeContent={unreadCount} color="error">
          <BellIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 400,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={clearAll}>
              Clear All
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <BellIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem alignItems="flex-start" sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ mr: 2, mt: 0.5 }}>
                    {getIcon(notification.type)}
                  </Box>
                  <ListItemText
                    primary={notification.message}
                    secondary={new Date(notification.timestamp).toLocaleTimeString()}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
